/**
 * Remote agent backend — interprets chat through the hosted Argo
 * agent-worker (the AWS → Claude pipeline) instead of the local parser.
 *
 * It implements the same `AgentBackend` interface as `localBackend`, so
 * ChatView neither knows nor cares which one it's talking to. If the
 * worker call fails for any reason it falls back to the deterministic
 * local parser, so the chat never goes dead — a flaky network downgrades
 * to local intent-matching rather than an error bubble.
 *
 * This module assumes the worker is configured; the backend *selection*
 * (remote when configured, else local) lives in `backend.ts`.
 */

import type { AgentBackend, AgentContext, AgentReply } from './types';
import { localBackend } from './localBackend';
import * as worker from './argoWorker';

export const remoteBackend: AgentBackend = {
  name: 'argo-worker',
  async interpret(message: string, context: AgentContext): Promise<AgentReply> {
    try {
      return await worker.interpret(message, context);
    } catch (err) {
      // Network/worker hiccup — degrade to the local parser rather than
      // failing the message. Logged so the fallback is visible in dev.
      // eslint-disable-next-line no-console
      console.warn('[Argo agent] worker interpret failed, using local parser:', err);
      return localBackend.interpret(message, context);
    }
  },
};
